import {
  AdapterInterface,
  AdapterCdoMceInterface,
  AdapterCdoLn1Interface,
} from "../../../interfaces/Adapter.interface";
import { AdapterCdoOpcInterface } from "../../../interfaces/AdapterCdoOpc.interface";
import { AdapterCdoSequentInterface } from "../../../interfaces/AdapterCdoSequent.interface";
import { AdapterCdoModbusInterface } from "../../../interfaces/AdapterCdoModbus.interface";
import { AdapterBacnetInterface } from "../../../interfaces/AdapterCdoBacnet.interface";

export interface CdoExtractionResult {
  cdoMce: AdapterCdoMceInterface | null;
  cdoLn1: AdapterCdoLn1Interface | null;
  cdoSequent: AdapterCdoSequentInterface | null;
  cdoOpc: AdapterCdoOpcInterface | null;
  cdoBacnet: AdapterBacnetInterface[] | null;
  modbus: AdapterCdoModbusInterface | null;
}

/**
 * Pure extraction of CDO state from an adapter record.
 *
 * Protocol-specific CDO types (MCE, LN1, Sequent, OPC) are mutually exclusive
 * — only one is populated based on `protocol_type`.
 *
 * BACnet CDO and bank mappings are no longer extracted from the adapter
 * because `bacnet_config` is always null and `bacnet_bank_mappings` was removed.
 */
export function extractCdoState(adapter: AdapterInterface): CdoExtractionResult {
  const result: CdoExtractionResult = {
    cdoMce: null,
    cdoLn1: null,
    cdoSequent: null,
    cdoOpc: null,
    cdoBacnet: null,
    modbus: null,
  };

  const proto = adapter.protocol_type.toLowerCase();
  const cdo = adapter.cdo_input;

  // CDO MCE
  if (
    proto === "mce" &&
    cdo &&
    "idl_file" in cdo &&
    "product_type" in cdo &&
    "event_reporting_address" in cdo
  ) {
    result.cdoMce = {
      idl_file: (cdo as AdapterCdoMceInterface).idl_file,
      product_type: (cdo as AdapterCdoMceInterface).product_type,
      event_reporting_address: (cdo as AdapterCdoMceInterface).event_reporting_address,
      event_mapping_file: (cdo as AdapterCdoMceInterface).event_mapping_file,
      event_mapping_data: (cdo as AdapterCdoMceInterface).event_mapping_data,
    };
  }
  // CDO LN1
  else if (
    (proto === "ln1" || proto === "cga") &&
    cdo &&
    "allowed_messages" in cdo &&
    "crc_override" in cdo &&
    "should_filter_duplicates" in cdo &&
    "terminating_zeroes_expected" in cdo
  ) {
    result.cdoLn1 = {
      allowed_messages: (cdo as AdapterCdoLn1Interface).allowed_messages,
      crc_override: (cdo as AdapterCdoLn1Interface).crc_override,
      should_filter_duplicates: (cdo as AdapterCdoLn1Interface).should_filter_duplicates,
      terminating_zeroes_expected: (cdo as AdapterCdoLn1Interface).terminating_zeroes_expected,
    };
  }
  // CDO SEQUENT
  else if (
    proto === "sequent" &&
    cdo &&
    "discrete_2_message" in cdo &&
    "dev_type" in cdo &&
    (cdo as AdapterCdoSequentInterface).discrete_2_message
  ) {
    result.cdoSequent = {
      discrete_2_message: (cdo as AdapterCdoSequentInterface).discrete_2_message,
      dev_type: (cdo as AdapterCdoSequentInterface).dev_type,
    };
  }
  // CDO OPC
  else if (
    proto === "opc" &&
    cdo &&
    "sites" in cdo &&
    "locations" in cdo &&
    "groups" in cdo &&
    "lifts" in cdo &&
    "car_num_to_name" in cdo &&
    "opc_server" in cdo &&
    "hostname" in cdo &&
    "location" in cdo
  ) {
    result.cdoOpc = {
      sites: (cdo as AdapterCdoOpcInterface).sites,
      locations: (cdo as AdapterCdoOpcInterface).locations,
      groups: (cdo as AdapterCdoOpcInterface).groups,
      lifts: (cdo as AdapterCdoOpcInterface).lifts,
      car_num_to_name: (cdo as AdapterCdoOpcInterface).car_num_to_name,
      opc_server: (cdo as AdapterCdoOpcInterface).opc_server,
      hostname: (cdo as AdapterCdoOpcInterface).hostname,
      location: (cdo as AdapterCdoOpcInterface).location,
    };
  }

  // MODBUS
  else if (proto === "modbus") {
    result.modbus = {
      slave_id: (cdo as AdapterCdoModbusInterface).slave_id,
      polling_interval: (cdo as AdapterCdoModbusInterface).polling_interval,
      msg_to_ln2_map: (cdo as AdapterCdoModbusInterface).msg_to_ln2_map,
      ln2_to_msg_map: (cdo as AdapterCdoModbusInterface).ln2_to_msg_map,
    };
  }

  return result;
}
